import { useMemo, useState } from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import { PiArrowBendUpLeft, PiCaretDown, PiCaretUp } from 'react-icons/pi'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { ActionsDropdown } from 'components/Buttons/ActionsDropdown'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { Attachment } from 'components/UI/Attachment/Attachment'
import { cn } from 'components/UI/cn'
import { ReplyMode } from 'components/FormUtils/EmailRecipientSelect'

import { SafeEmailRenderer } from './SafeEmailRenderer'
import { EmailAction } from './types'

/**
 * Generic message shape consumed by `ThreadMessageCard`. Callers map their
 * native shape (e.g. `EmailMessage`, `Reply`) to this via a thin adapter.
 */
export interface ThreadMessageData {
  id: string | number
  sender: { name: string; email: string }
  toRecipients: Array<{ name: string; email: string }>
  ccRecipients: Array<{ name: string; email: string }>
  /** ISO date string. Null/empty renders the card without a timestamp. */
  date: string | null | undefined
  /** HTML body (preferred). */
  htmlBody?: string | null
  /** Plain-text fallback when `htmlBody` is empty. */
  textBody?: string | null
  attachments?: Array<{
    key: string | number
    filename: string
    size?: number
    contentType?: string
    downloadUrl?: string
    /** Inline attachments (CID refs inside the HTML) are filtered out. */
    isInline?: boolean
  }>
}

export interface ThreadMessageCardProps {
  message: ThreadMessageData
  /** Suppresses the bottom divider on the final message of a thread list. */
  isLast?: boolean
  /** When true, the header toggles body+attachments visibility on click. */
  collapsible?: boolean
  /** Initial expand state when `collapsible`. Defaults to `true`. */
  defaultExpanded?: boolean
  /** Fires on the transition from collapsed → expanded (e.g. auto-mark-read). */
  onExpand?: () => void
  /** Kebab menu items (owner-style 3-dot menu). Nothing renders when omitted. */
  menuItems?: EmailAction[]
  /** Arrow-icon dropdown with Reply / Reply All / Forward. Hidden when omitted. */
  onQuickCompose?: (mode: ReplyMode) => void
}

function getInitials(name: string): string {
  return (
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'UN'
  )
}

/**
 * Format a date as "9:14 AM (8 hours ago)".
 *
 * Returns an empty string for null/empty/invalid inputs — date-fns `format`
 * throws `RangeError` on invalid dates, and crashing the whole card on a
 * single bad reply (e.g. an untracked reply with a missing timestamp) is
 * worse than showing the name with no time.
 */
function formatTimestamp(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return ''
  return `${format(date, 'h:mm a')} (${formatDistanceToNow(date, { addSuffix: true })})`
}

function summarizeRecipients(
  recipients: Array<{ name: string; email: string }>
): string {
  return recipients.map((r) => r.name || r.email).join(', ')
}

function formatContact(contact: { name: string; email: string }): string {
  return contact.name ? `${contact.name} <${contact.email}>` : contact.email
}

/**
 * Unified message card used by both the company email inbox and the campaign
 * reply inbox. Visual chrome (avatar, sender header, timestamp, expandable
 * recipients, body, attachments, kebab/quick-reply) is identical across both.
 *
 * Callers pass a `ThreadMessageData` (adapter) plus context-specific menu
 * items. Optional `collapsible` keeps the campaign side's expand/collapse UX
 * on multi-message threads.
 */
export function ThreadMessageCard({
  message,
  isLast,
  collapsible = false,
  defaultExpanded = true,
  onExpand,
  menuItems,
  onQuickCompose,
}: ThreadMessageCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [recipientsExpanded, setRecipientsExpanded] = useState(false)
  const modalDepth = useModalDepth()
  const zIndex = useMemo(() => getDropdownZIndex(modalDepth), [modalDepth])

  const { sender, toRecipients, ccRecipients, date, attachments } = message
  const body = message.htmlBody || message.textBody || ''
  const isHtml = !!message.htmlBody
  const nonInlineAttachments = (attachments ?? []).filter((a) => !a.isInline)

  const handleHeaderClick = () => {
    if (!collapsible) return
    const willExpand = !expanded
    setExpanded(willExpand)
    if (willExpand) onExpand?.()
  }

  const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
    if (!collapsible) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleHeaderClick()
    }
  }

  // Collapse toggle lives on the outer container; children need to swallow
  // clicks so buttons/menus don't also flip the collapsed state.
  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

  return (
    <div
      className={cn(
        'px-6 pt-4 pb-5',
        !isLast && 'border-b border-line-subtle',
        collapsible && 'group cursor-pointer'
      )}
      onClick={handleHeaderClick}
      onKeyDown={handleHeaderKeyDown}
      role={collapsible ? 'button' : undefined}
      tabIndex={collapsible ? 0 : undefined}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-fg-muted flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">
            {getInitials(sender.name || sender.email)}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold text-fg truncate">
              {sender.name || sender.email}
            </span>
            {sender.email && sender.name && (
              <span className="text-sm text-fg-secondary truncate">
                &lt;{sender.email}&gt;
              </span>
            )}

            <div
              className="ml-auto flex items-center gap-1 flex-shrink-0"
              onClick={stopPropagation}
              onKeyDown={stopPropagation}
            >
              <span className="text-xs text-fg-secondary">
                {formatTimestamp(date)}
              </span>
              {onQuickCompose && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="p-1 rounded hover:bg-surface-alt text-fg-secondary transition-colors"
                      title="Reply"
                    >
                      <PiArrowBendUpLeft className="size-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" style={{ zIndex }}>
                    <DropdownMenuItem onSelect={() => onQuickCompose('reply')}>
                      Reply
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => onQuickCompose('reply_all')}
                    >
                      Reply All
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => onQuickCompose('forward')}
                    >
                      Forward
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {menuItems && menuItems.length > 0 && (
                <ActionsDropdown
                  actions={menuItems}
                  className="size-6 inline-flex items-center justify-center rounded hover:bg-surface-alt has-[[data-state=open]]:bg-surface-alt transition-colors text-fg-secondary"
                />
              )}
            </div>
          </div>

          {toRecipients.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                stopPropagation(e)
                setRecipientsExpanded((prev) => !prev)
              }}
              className="flex items-center gap-0.5 text-sm text-fg-secondary hover:text-fg transition-colors mt-0.5"
            >
              <span className="truncate max-w-[300px]">
                to {summarizeRecipients(toRecipients)}
              </span>
              {recipientsExpanded ? (
                <PiCaretUp className="size-3 flex-shrink-0" />
              ) : (
                <PiCaretDown className="size-3 flex-shrink-0" />
              )}
            </button>
          )}

          {recipientsExpanded && (
            <div className="mt-2 text-xs text-fg-secondary space-y-1">
              {toRecipients.length > 0 && (
                <div>
                  <span className="font-medium">To: </span>
                  {toRecipients.map(formatContact).join(', ')}
                </div>
              )}
              {ccRecipients.length > 0 && (
                <div>
                  <span className="font-medium">Cc: </span>
                  {ccRecipients.map(formatContact).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {collapsible && !expanded && (
        <p className="pl-[52px] mt-0.5 text-xs text-fg-secondary truncate">
          {(
            message.textBody ||
            message.htmlBody?.replace(/<[^>]+>/g, ' ') ||
            ''
          )
            .replace(/\s+/g, ' ')
            .trim()}
        </p>
      )}

      {expanded && (
        <div
          className="mt-4 pl-[52px] text-sm leading-relaxed"
          onClick={stopPropagation}
          onKeyDown={stopPropagation}
        >
          {isHtml ? (
            <SafeEmailRenderer html={body} />
          ) : (
            <p className="whitespace-pre-wrap">{body}</p>
          )}
        </div>
      )}

      {expanded && nonInlineAttachments.length > 0 && (
        <div className="mt-3 pl-[52px] flex flex-wrap gap-2">
          {nonInlineAttachments.map((att) => (
            <Attachment
              key={att.key}
              filename={att.filename}
              size={att.size ?? 0}
              contentType={att.contentType ?? ''}
            />
          ))}
        </div>
      )}
    </div>
  )
}
